/**
 * Calculation Service
 * 
 * Handles usage tracking, limit enforcement, and calculation analytics.
 * Integrates with Supabase database to track user calculations and enforce the 5-calculation limit.
 */

import { supabase } from './supabaseClient';
import type { TravelMode } from '../types';
import type { Coordinates } from '../types';

/**
 * Parameters for tracking a calculation
 */
export interface CalculationParams {
  userId: string;
  originLat: number;
  originLng: number;
  maxTime: number;
  travelMode: TravelMode;
}

/**
 * User usage information
 */
export interface UserUsage {
  userId: string;
  calculationCount: number;
  limit: number;
  remaining: number;
}

/**
 * Checks if user can make a calculation (under limit)
 * @param userId - User ID (UUID)
 * @returns True if user can make calculation, false if limit reached
 * @throws Error if database query fails
 */
export async function canMakeCalculation(userId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('user_profiles')
    .select('calculation_count, calculation_limit')
    .eq('id', userId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      // User profile not found - treat as can make calculation (will be created)
      return true;
    }
    throw new Error(`Failed to check usage limit: ${error.message}`);
  }

  const limit = data.calculation_limit ?? 5;
  return (data.calculation_count ?? 0) < limit;
}

/**
 * Tracks a calculation and increments usage counter
 * 
 * IMPORTANT: This function uses a database trigger for atomic enforcement.
 * The trigger `increment_on_calculation_insert` increments the count AFTER successful insert,
 * ensuring atomicity and preventing race conditions.
 * 
 * Uses reverse geocoding to get location details (country, region, city) from the calculation coordinates.
 * This tracks WHERE the calculation was performed, not where the user is from.
 * 
 * @param params - Calculation parameters
 * @throws Error with 'USAGE_LIMIT_EXCEEDED' message if limit exceeded
 * @throws Error if database operation fails
 */
export async function trackCalculation(params: CalculationParams): Promise<void> {
  if (import.meta.env.VITE_DEBUG === 'true') {
    console.log('[trackCalculation] Starting for userId:', params.userId);
  }
  
  // Get location details from reverse geocoding (coordinates → location)
  // This tells us WHERE the calculation was performed (country down to street level)
  const { reverseGeocodeWithDetails } = await import('./geocodingService');
  const locationDetailsPromise = reverseGeocodeWithDetails({
    lat: params.originLat,
    lng: params.originLng,
  } as Coordinates).catch(() => ({
    address: null,
    country: null,
    region: null,
    district: null,
    city: null,
    neighborhood: null,
    postcode: null,
    street: null,
  }));

  // Use timeout to ensure we don't wait too long
  const locationDetails = await Promise.race([
    locationDetailsPromise,
    new Promise<{ address: string | null; country: string | null; region: string | null; district: string | null; city: string | null; neighborhood: string | null; postcode: string | null; street: string | null }>((resolve) =>
      setTimeout(() => resolve({ address: null, country: null, region: null, district: null, city: null, neighborhood: null, postcode: null, street: null }), 2000)
    ),
  ]);

  if (import.meta.env.VITE_DEBUG === 'true') {
    console.log('[trackCalculation] Location details collected:', locationDetails);
  }

  // Insert calculation record - trigger will atomically increment count
  // If limit is exceeded, the trigger will raise an exception
  if (import.meta.env.VITE_DEBUG === 'true') {
    console.log('[trackCalculation] Attempting to insert calculation record...');
  }
  const { error: insertError } = await supabase.from('calculations').insert({
    user_id: params.userId,
    origin_lat: params.originLat,
    origin_lng: params.originLng,
    max_time: params.maxTime,
    travel_mode: params.travelMode,
    country: locationDetails.country,
    region: locationDetails.region,
    district: locationDetails.district,
    city: locationDetails.city,
    neighborhood: locationDetails.neighborhood,
    postcode: locationDetails.postcode,
    street: locationDetails.street,
  });

  if (insertError) {
    console.error('[trackCalculation] Insert failed:', insertError);
    // Check for usage limit error (database trigger raises P0001 error code)
    if (insertError.code === 'P0001' || 
        insertError.message.includes('limit') || 
        insertError.message.includes('Usage limit') || 
        insertError.message.includes('exceeded')) {
      throw new Error('USAGE_LIMIT_EXCEEDED');
    }
    throw new Error(`Failed to track calculation: ${insertError.message}`);
  }
  
  if (import.meta.env.VITE_DEBUG === 'true') {
    console.log('[trackCalculation] Insert successful!');
  }
}

/**
 * Gets user usage information
 * @param userId - User ID (UUID)
 * @returns User usage information with count, limit, and remaining
 * @throws Error if database query fails
 */
export async function getUserUsage(userId: string): Promise<UserUsage> {
  const { data, error } = await supabase
    .from('user_profiles')
    .select('calculation_count, calculation_limit')
    .eq('id', userId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      // User profile not found - return default usage
      return {
        userId,
        calculationCount: 0,
        limit: 5,
        remaining: 5,
      };
    }
    throw new Error(`Failed to fetch user usage: ${error.message}`);
  }

  const calculationCount = data.calculation_count ?? 0;
  const limit = data.calculation_limit ?? 5;

  return {
    userId,
    calculationCount,
    limit,
    remaining: Math.max(0, limit - calculationCount),
  };
}


